import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Slide,
  TextField,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { MDBBtn } from "mdb-react-ui-kit";
import SubscriptionService from "../api/SubscriptionService";
import CustomSnackbar from "../components/common/CustomSnackbar";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AdminService from "../api/AdminService";
import { useNavigate } from "react-router-dom";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SubscriptionsPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmationDeleteDialog, setOpenConfirmationDeleteDialog] = useState(false);
  const [error, setError] = useState(null);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState(null);

  const [initialSubscriptions, setInitialSubscriptions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [reorderedSubscriptions, setReorderedSubscriptions] = useState([]);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [users, setUsers] = useState([]);

  const imageWidth = "300px";

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      loadData();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  const handleConfirmationDeleteDialog = () => {
    setSubscriptionToDelete(currentSubscription.id);
    setOpenConfirmationDeleteDialog(true);
  };

  const handleCloseConfirmationDeleteDialog = (agree) => {
    if (agree && subscriptionToDelete?.toString()) {
      if (users.some(user => user.subscriptionId === subscriptionToDelete)) {
        showSnackbar("Неможливо видалити підписку, яка вже використовується.", "error");
      } else {
        deleteSubscription(subscriptionToDelete).then(r => {
          setOpenDialog(false);
          setSubscriptionToDelete(null);
          setCurrentSubscription(null);
          fetchSubscriptions();
        });
      }
    }
    setOpenConfirmationDeleteDialog(false);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const reorderedSubscriptions = Array.from(subscriptions);
    const [movedSubscription] = reorderedSubscriptions.splice(source.index, 1);
    reorderedSubscriptions.splice(destination.index, 0, movedSubscription);
    const updatedSubscriptions = reorderedSubscriptions.map((subscription, index) => ({
      ...subscription,
      orderNumber: index + 1
    }));
    setSubscriptions(updatedSubscriptions);
    setReorderedSubscriptions(updatedSubscriptions);
  };

  const handleReorderedSubscriptionsSave = async () => {
    try {
      await SubscriptionService.updateSubscriptionsOrder(reorderedSubscriptions);
      await fetchSubscriptions();
      showSnackbar("Сортування підписок успішно змінено!", "success");
    } catch (error) {
      setError("Failed to update subscriptions ordering: " + error.message);
      showSnackbar("Не вдалося змінити сортування підписок: " + error.message, "error");
    } finally {
      setReorderedSubscriptions([]);
    }
  };

  const isOrderChanged =
    initialSubscriptions.length > 0 &&
    reorderedSubscriptions.length > 0 &&
    !initialSubscriptions.every((initialSubscription) => {
      const currentSubscription = reorderedSubscriptions.find(
        (subscription) => subscription.id === initialSubscription.id
      );
      return (
        currentSubscription &&
        initialSubscription.orderNumber === currentSubscription.orderNumber
      );
    });

  const fetchSubscriptions = async () => {
    try {
      const fetchedSubscriptions = await SubscriptionService.getSubscriptions();
      if (fetchedSubscriptions.length === 0) {
        setError("Підписки не знайдені.");
      }
      const subscriptionsWithImages = fetchedSubscriptions.map((subscription) => {
        return subscription;
      });
      setSubscriptions(subscriptionsWithImages);
      setInitialSubscriptions(subscriptionsWithImages);
    } catch (error) {
      setError("Failed to load subscriptions: " + error.message);
      showSnackbar("Помилка при спробі завантижити підписки: " + error.message, "error");
    }
  };

  const fetchUsers = async () => {
    try {
      const userList = await AdminService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      setError("Failed to load users data: " + error.message);
      showSnackbar("Помилка при спробі завантижити користувачів: " + error.message, "error");
    }
  };

  const handleSave = async () => {
    try {
      const { image, imageUrl, ...subscriptionData } = currentSubscription;
      if (currentSubscription?.id?.toString()) {
        await SubscriptionService.updateSubscription(subscriptionData);
        showSnackbar("Підписку успішно оновлено!", "success");
      } else {
        await SubscriptionService.createSubscription(currentSubscription);
        showSnackbar("Підписку успішно створено!", "success");
      }
      await fetchSubscriptions();
      handleCloseDialog();
    } catch (error) {
      setError("Failed to save subscription: " + error.message);
      showSnackbar("Помилка при спробі зберегти підписку: " + error.message, "error");
    }
  };

  const deleteSubscription = async (id) => {
    try {
      await SubscriptionService.deleteSubscription(id);
      setSubscriptions(subscriptions.filter(sub => sub.id !== id));
      showSnackbar("Підписку успішно видалено!", "success");
    } catch (error) {
      setError("Failed to delete subscription: " + error.message);
      showSnackbar("Помилка при спробі видалити підписку: " + error.message, "error");
    }
  };

  const handleOpenDialog = (subscription) => {
    const newSubscription = subscription || {
      name: '',
      description: '',
      storageLimit: 0,
      familyMembersLimit: 0
    };
    setCurrentSubscription(newSubscription);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSubscription(null);
    setError(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await fetchSubscriptions();
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
        <Typography variant="h5">Підписки</Typography>
        {error ? (
          <div
            className="container alert alert-warning text-center mt-3"
            role="alert"
          >
            {error}
          </div>
        ) : (
          loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="85vh">
              <CircularProgress/>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              {subscriptions.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="subscriptionsDroppable" direction={"horizontal"}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          display: "flex",
                          overflowX: "auto",
                          overflowY: "clip",
                          minHeight: "300px",
                          padding: "4px 8px",
                          marginTop: "10px"
                        }}
                      >
                        {subscriptions.map((subscription, index) => (
                          <Draggable key={subscription.id} draggableId={subscription?.id.toString()} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  marginRight: "30px",
                                  marginBottom: "10px"
                                }}
                              >
                                <Card key={subscription.id}
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        width: imageWidth,
                                        position: "relative",
                                        borderRadius: "15px",
                                        padding: "10px",
                                        textAlign: "center"
                                      }}>

                                  <Typography variant="h6" style={{ marginBottom: "10px" }}>
                                    {subscription.title}
                                  </Typography>

                                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: "15px" }}>
                                    {"Опис:"} <br/> {subscription.description}
                                  </Typography>

                                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: "5px" }}>
                                    {"Кількість доступного місця: " + subscription.storageLimit || "0"} MB
                                  </Typography>

                                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: "5px" }}>
                                    {"Кількість доступних акаунтів у родині: " + subscription.familyMembersLimit}
                                  </Typography>

                                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: "5px" }}>
                                    {"Вартість на місяць: " + parseFloat(subscription.price || "0")} грн
                                  </Typography>

                                  <Chip
                                    label={subscription.active ? "Активна" : "Неактивна"}
                                    style={{
                                      backgroundColor: subscription.active ? "lightgreen" : "lightpink",
                                      color: "black",
                                      marginBottom: "5px"
                                    }}
                                  />

                                  <CardActions style={{ justifyContent: "center" }}>
                                    <Button
                                      fullWidth
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => handleOpenDialog(subscription)}
                                      startIcon={<EditIcon/>}
                                    >
                                      Редагувати підписку
                                    </Button>
                                  </CardActions>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {subscriptions.length > 0 && (
                <Button sx={{ marginTop: 1.5, width: { sm: '420px', xs: '100%' } }}
                        variant="contained"
                        color="success"
                        onClick={handleReorderedSubscriptionsSave}
                        disabled={!isOrderChanged}>
                  Зберегти нове сортування
                </Button>
              )}
              <Button sx={{ marginTop: 1.5, width: { sm: '420px', xs: '100%' } }} variant="contained" color="primary"
                      onClick={() => handleOpenDialog()}>
                Додати нову підписку
              </Button>
            </Box>
          )
        )}
      </Box>
      )

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{currentSubscription?.id?.toString() ? "Редагувати підписку" : "Додати нову підписку"}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon fontSize="large"/>
        </IconButton>
        <DialogContent>
          <TextField
            size="small"
            label="Назва"
            fullWidth
            value={currentSubscription?.title || ""}
            onChange={(e) => setCurrentSubscription({ ...currentSubscription, title: e.target.value })}
          />
          <TextField
            size="small"
            margin="dense"
            label="Опис"
            fullWidth
            value={currentSubscription?.description || ""}
            onChange={(e) => setCurrentSubscription({ ...currentSubscription, description: e.target.value })}
          />
          <TextField
            size="small"
            margin="dense"
            label="Ліміт виділеного місця"
            fullWidth
            value={currentSubscription?.storageLimit || "0"}
            onChange={(e) => setCurrentSubscription({
              ...currentSubscription,
              storageLimit: parseFloat(e.target.value || "0")
            })}
          />
          <TextField
            size="small"
            margin="dense"
            label="Ліміт членів родини"
            fullWidth
            value={currentSubscription?.familyMembersLimit || "0"}
            onChange={(e) => setCurrentSubscription({
              ...currentSubscription,
              familyMembersLimit: parseFloat(e.target.value || "0")
            })}
          />
          <TextField
            size="small"
            margin="dense"
            label="Вартість"
            fullWidth
            value={currentSubscription?.price || "0"}
            onChange={(e) => setCurrentSubscription({
              ...currentSubscription,
              price: parseFloat(e.target.value || "0")
            })}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <FormControl component="fieldset" style={{ marginTop: '5px', width: '100%' }}>
              <RadioGroup
                row
                aria-label="status"
                name="status"
                value={currentSubscription?.active ? "active" : "inactive"}
                onChange={(e) => setCurrentSubscription({
                  ...currentSubscription,
                  active: e.target.value === "active"
                })}
                style={{ justifyContent: 'space-evenly', width: '100%' }}
              >
                <FormControlLabel value="active" control={<Radio/>} label="Активна"/>
                <FormControlLabel value="inactive" control={<Radio/>} label="Неактивна"/>
              </RadioGroup>
            </FormControl>
          </Box>

          {currentSubscription?.id?.toString() && (
            <Button
              className="mt-3"
              fullWidth
              variant="outlined"
              color="error"
              onClick={() => handleConfirmationDeleteDialog()}
              startIcon={<DeleteIcon/>}
            >
              Видалити підписку
            </Button>
          )}
        </DialogContent>

        <DialogActions
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <MDBBtn
                block
                color="success"
                onClick={handleSave}
              >
                Зберегти
              </MDBBtn>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MDBBtn
                block
                color="secondary"
                onClick={handleCloseDialog}
              >
                Відмінити
              </MDBBtn>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmationDeleteDialog}
        onClose={() => handleCloseConfirmationDeleteDialog(false)}
        TransitionComponent={Transition}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>Підтвердіть дію</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleCloseConfirmationDeleteDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon fontSize="large"/>
        </IconButton>
        <IconButton
          aria-label="close"
          onClick={() => handleCloseConfirmationDeleteDialog(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon fontSize="large"/>
        </IconButton>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            className="text-center"
          >
            {`Ви впевнені, що хочете видалити підписку '${currentSubscription?.title}' ?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <MDBBtn
                block
                color="danger"
                onClick={() => handleCloseConfirmationDeleteDialog(true)}
              >
                Видалити
              </MDBBtn>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <MDBBtn
                block
                color="secondary"
                onClick={() => handleCloseConfirmationDeleteDialog(false)}
              >
                Скасувати
              </MDBBtn>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        message={snackbarMessage}
        severity={snackbarSeverity}
        open={openSnackbar}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default SubscriptionsPage;
